/* NW Wellness — Search overlay
   Opens on 'search:open' event from the header. Predictive search
   via Shopify's `/search/suggest.json` endpoint. Debounced. */

(function () {
  const root = window.Shopify?.routes?.root || '/';

  class NWSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-nw-search-input]');
      this.form = this.querySelector('[data-nw-search-form]');
      this.hint = this.querySelector('[data-nw-search-hint]');
      this.results = this.querySelector('[data-nw-search-results]');
      this.empty = this.querySelector('[data-nw-search-empty]');
      this.cols = {
        products:    this.querySelector('[data-nw-col-products] [data-nw-list]'),
        collections: this.querySelector('[data-nw-col-collections] [data-nw-list]'),
        articles:    this.querySelector('[data-nw-col-articles] [data-nw-list]'),
      };
      this.closeBtn = this.querySelector('[data-nw-search-close]');
      this.allWrap = this.querySelector('[data-nw-search-all]');
      this.allLink = this.querySelector('[data-nw-search-all-link]');

      this.openHandler = (e) => this.open(e.detail && e.detail.trigger);
      this.keyHandler = (e) => {
        if (e.key === 'Escape') this.close();
      };

      document.addEventListener('search:open', this.openHandler);
      if (window.location.hash === '#search') this.open();
      document.addEventListener('keydown', this.keyHandler);
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.input) this.input.addEventListener('input', this.debounce(() => this.runSearch(), 200));
      if (this.form) this.form.addEventListener('submit', (e) => {
        // Let the form submit to /search?q=... naturally — gives a full results page
        if (!this.input.value.trim()) e.preventDefault();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('search:open', this.openHandler);
      document.removeEventListener('keydown', this.keyHandler);
    }

    open(trigger) {
      // Remember the opener so close() can return focus to it
      this.triggerEl = trigger || document.querySelector('[data-nw-action="search"]');
      this.hidden = false;
      // Focus synchronously so the tap gesture carries through —
      // iOS only opens the keyboard for focus inside a user gesture.
      if (this.input) this.input.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        this.setAttribute('data-open', '');
        document.body.classList.add('nw-drawer-open');
        // Trap focus inside the overlay (global.js utility, same as DetailsModal)
        if (typeof trapFocus === 'function') {
          trapFocus(this, this.input || this);
        }
      });
    }

    close() {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      document.body.classList.remove('nw-drawer-open');
      // Release the trap and return focus to the trigger
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.triggerEl);
      } else if (this.triggerEl) {
        this.triggerEl.focus();
      }
      setTimeout(() => { this.hidden = true; }, 250);
    }

    debounce(fn, ms) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), ms);
      };
    }

    async runSearch() {
      const q = (this.input?.value || '').trim();
      if (!q) {
        this.hint.hidden = false;
        this.results.hidden = true;
        this.empty.hidden = true;
        if (this.allWrap) this.allWrap.hidden = true;
        return;
      }
      this.hint.hidden = true;
      try {
        const url = new URL(root + 'search/suggest.json', window.location.origin);
        url.searchParams.set('q', q);
        url.searchParams.set('resources[type]', 'product,collection,article');
        url.searchParams.set('resources[limit]', '6');
        const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        const r = data.resources && data.resources.results || {};
        this.render('products',    r.products    || []);
        this.render('collections', r.collections || []);
        this.render('articles',    r.articles    || []);
        const total = (r.products?.length || 0) + (r.collections?.length || 0) + (r.articles?.length || 0);
        this.results.hidden = total === 0;
        this.empty.hidden = total !== 0;
        if (this.allWrap) {
          this.allWrap.hidden = total === 0;
          if (this.allLink) this.allLink.href = root + 'search?q=' + encodeURIComponent(q);
        }
      } catch (e) {
        this.results.hidden = true;
        this.empty.hidden = false;
      }
    }

    render(kind, items) {
      const list = this.cols[kind];
      if (!list) return;
      list.innerHTML = items.map((item) => this.renderItem(kind, item)).join('');
    }

    renderItem(kind, item) {
      const safe = (s) => (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      if (kind === 'products') {
        const img = item.featured_image?.url || item.image || '';
        const price = item.price ? this.formatMoney(item.price) : '';
        return `
          <li><a href="${safe(item.url)}">
            ${img ? `<img src="${safe(img)}" alt="">` : '<span></span>'}
            <span>
              <span class="nw-search__title">${safe(item.title)}</span>
              ${item.vendor ? `<span class="nw-search__sub">${safe(item.vendor)}</span>` : ''}
            </span>
            ${price ? `<span class="nw-search__price">${price}</span>` : ''}
          </a></li>`;
      }
      // collections + articles
      return `
        <li><a href="${safe(item.url)}">
          <span></span>
          <span>
            <span class="nw-search__title">${safe(item.title)}</span>
            ${item.body ? `<span class="nw-search__sub">${safe(item.body).slice(0, 80)}</span>` : ''}
          </span>
          <span></span>
        </a></li>`;
    }

    formatMoney(value) {
      // Shopify's suggest.json returns price as a string in shop currency (e.g. "12.99")
      if (typeof value === 'string') return '£' + value;
      return '£' + (Number(value) / 100).toFixed(2);
    }
  }

  if (!customElements.get('nw-search')) {
    customElements.define('nw-search', NWSearch);
  }
})();
